<template>
  <CollapseView
    ref="collapse"
    :open="open && !disabled"
    @open="$emit('open')"
    @close="$emit('close')"
    :data-e2e="dataE2e"
    :animation="animation"
    class="m-b-10"
    :class="{
      'is-disabled': disabled,
      panel: isPanel,
      'has-background-secondary': hasBackground,
      'is-shadowless': isShadowless,
      'has-border-light': !isBorderless,
      'expansion-disabled': isExpansionDisabled,
      [animationClass]: true,
      [borderRadiusClass]: true
    }"
  >
    <template #trigger="triggerProps">
      <div
        class="trigger is-flex is-align-items-center"
        :class="{
          open: triggerProps.open,
          'is-borderless': hasBorderlessHeader,
          'is-flex-direction-row-reverse': iconPosition === Position.RIGHT,
          'is-flex-direction-row is-justify-content-start': iconPosition === Position.LEFT
        }"
        :data-e2e="AppCypressAttribute.buttons.COLLAPSE_TRIGGER"
      >
        <slot name="pre-icon-header" />
        <div
          v-if="showIcon"
          class="icon"
          :class="{
            animate: animate,
            rotate: triggerProps.open
          }"
          :data-e2e="AppCypressAttribute.buttons.COLLAPSE_TRIGGER_ICON"
        >
          <IconView pack="cls" size="is-small" custom-class="cls-caret-right" />
        </div>
        <slot name="header" :trigger-props="triggerProps" />
      </div>
      <slot name="preview" />
    </template>
    <div class="p-2">
      <slot />
    </div>
  </CollapseView>
</template>

<script>
import { CollapseEvent } from '@/modules/core/app/constants/CollapseEvent';
import { AppCypressAttribute } from '/@/modules/core/app/constants/AppCypressAttribute';
import IconView from '@/modules/core/ui/components/wrappers/IconView.vue';
import CollapseView from '@/modules/core/ui/components/wrappers/CollapseView.vue';
import { AnimationType } from '@/modules/core/app/enums/AnimationType';
import { Position } from '@/modules/core/app/constants/Position';
export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'Collapse',
  emits: ['open', 'close'],
  slotsDefinition: {
    preIconHeader: {},
    header: { triggerProps: Object },
    preview: {}
  },
  components: { IconView, CollapseView },
  props: {
    open: {
      type: Boolean,
      default: true
    },
    animate: {
      type: Boolean,
      default: true
    },
    ariaId: {
      type: String
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    iconPosition: {
      type: String,
      default: Position.LEFT,
      validator: (value) => [Position.LEFT, Position.RIGHT].includes(value)
    },
    isPanel: {
      type: Boolean,
      default: false
    },
    isBorderless: {
      type: Boolean,
      default: false
    },
    hasBorderlessHeader: {
      type: Boolean,
      default: true
    },
    isShadowless: {
      type: Boolean,
      default: false
    },
    hasBackground: {
      type: Boolean,
      default: false
    },
    dataE2e: {
      type: String,
      default: null
    },
    isExpansionDisabled: {
      type: Boolean,
      default: false
    },
    collapseId: {
      type: String,
      default: null
    },
    animation: {
      type: String,
      default: AnimationType.EXPAND
    },
    borderRadiusSize: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    }
  },
  static: {
    AppCypressAttribute,
    Position
  },
  events() {
    return {
      [CollapseEvent.COLLAPSE]: this.onCollapse,
      [CollapseEvent.EXPAND]: this.onExpand
    };
  },
  computed: {
    isOpen() {
      return this.$refs.collapse.isOpen;
    },
    borderRadiusClass() {
      return `is-radius-${this.borderRadiusSize}`;
    },
    animationClass() {
      return `is-animation-${this.animation}`;
    }
  },
  methods: {
    toggle() {
      this.$refs.collapse.toggle();
    },
    onCollapse(collapseId) {
      if (collapseId !== this.collapseId || !this.isOpen) {
        return;
      }

      this.toggle();
    },
    onExpand(collapseId) {
      if (collapseId !== this.collapseId || this.isOpen) {
        return;
      }

      this.toggle();
    }
  }
};
</script>

<style scoped>
div.trigger {
  padding: var(--ce-12px) var(--ce-15px);

  &.is-borderless {
    border-bottom: unset !important;
  }

  &.open {
    border-bottom: var(--ce-1px) solid var(--ce-grey-lightest);
  }

  .icon {
    color: var(--ce-primary-dark-20);
    font-size: var(--ce-14px);

    &.animate {
      transition: transform 200ms ease;
      transform-origin: center;
    }

    &.rotate {
      transform: rotate(90deg);
    }
  }
}

/* Hover highlights only the innermost card under the cursor: a card containing a
   hovered nested .collapse is excluded via :has(). !important plus this specificity
   out-ranks the global .has-border-light trait, while :not(.has-border-primary)
   (Template Builder drag) and :not(.is-borderless) keep the global traits winning. */
.collapse:not(.is-disabled):not(.is-borderless):not(.has-border-primary):hover:not(:has(.collapse:hover)) {
  border-color: var(--ce-border-focus) !important;
}

.expansion-disabled :deep(.collapse-content) {
  display: none !important;
}

/* Height-grow open/close (AnimationType.EXPAND). Buefy hides the body with an inline
   display:none, which no transition can run on, so the wrapper stays displayed and its
   single grid row grows between 0fr and 1fr, keyed off that inline style. Guarded with
   :not(.expansion-disabled) so the display:none !important of a disabled expansion
   always wins despite both rules sharing the same specificity. */
.is-animation-expand:not(.expansion-disabled) :deep(.collapse-content) {
  display: grid !important;
  grid-template-rows: 1fr;
  overflow: visible;
  visibility: visible;
  transition:
    grid-template-rows 200ms ease-out,
    overflow 0s linear 200ms,
    visibility 0s linear;
  transition-behavior: allow-discrete;
}

.is-animation-expand :deep(.collapse-content[style*="display: none"]) {
  grid-template-rows: 0fr;
  overflow: hidden;
  visibility: hidden;
  transition:
    grid-template-rows 320ms ease-out,
    overflow 0s linear,
    visibility 0s linear 320ms;
}

/* The single grid item must be allowed to shrink below its content height. */
.is-animation-expand :deep(.collapse-content) > div {
  min-height: 0;
}

/* A closed card keeps the item's vertical padding as a blank strip under the header,
   because padding floors the 0fr row even at a zero height. It is dropped only once the
   leave animation has finished and v-show has applied its display:none. */
.is-animation-expand :deep(.collapse-content[style*="display: none"]) > div {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* Users who prefer reduced motion get instant open/close and an instant caret. */
@media (prefers-reduced-motion: reduce) {
  .is-animation-expand :deep(.collapse-content),
  div.trigger .icon.animate {
    transition: none !important;
  }
}
</style>
